import { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { QueryClient, QueryClientProvider, useQueryClient } from '@tanstack/react-query';
import { MainLayout } from '@components/layout';
import { KanbanBoard, TaskModal, ProjectModal, ClientModal } from '@components/features';
import { SprintsView, TeamView, MetricsView, RoadmapView, CalendarView, LoginPage, ClientPortal, ClientPortalLogin, SettingsPage, TaskDetailPage, SprintDetailView, MeetingsView } from '@pages';
import { useUIStore, useProjectStore, useAuthStore, useThemeStore } from '@store';
import { Card, Spinner } from '@components/ui';
import { clientService } from '@services';
import {
  useProjects,
  useTasks,
  useUpdateTaskStatus,
  useCreateTask,
  useUpdateTask,
  useDeleteTask,
  useSprints,
  useEpics,
  useProjectMembers,
  useCreateProject,
  useUpdateProject,
  useClients,
  useCreateClient,
  useUpdateClient,
  useTaskStatuses,
  useSocket,
} from '@hooks';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
    },
  },
});

// Dashboard Stats Component
function DashboardStats({ tasks, isLoading }) {
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {[1, 2, 3, 4].map((i) => (
          <Card key={i} padding="md">
            <div className="animate-pulse">
              <div className={`h-4 ${isDark ? 'bg-slate-700' : 'bg-slate-200'} rounded w-20 mb-2`}></div>
              <div className={`h-8 ${isDark ? 'bg-slate-700' : 'bg-slate-200'} rounded w-16`}></div>
            </div>
          </Card>
        ))}
      </div>
    );
  }

  const totalTasks = tasks?.length || 0;
  const inProgress = tasks?.filter((t) => t.status === 'progress' || t.status === 'review').length || 0;
  const completed = tasks?.filter((t) => t.status === 'done').length || 0;
  const totalPoints = tasks?.reduce((sum, t) => sum + (t.points || 0), 0) || 0;
  const completedPoints = tasks?.filter((t) => t.status === 'done').reduce((sum, t) => sum + (t.points || 0), 0) || 0;

  const stats = [
    { label: 'Total Tareas', value: totalTasks, change: `${completed} completadas`, icon: '📋', color: '#667eea' },
    { label: 'En Progreso', value: inProgress, change: `${Math.round((inProgress / totalTasks) * 100) || 0}% del total`, icon: '⚡', color: '#f59e0b' },
    { label: 'Completadas', value: completed, change: `${Math.round((completed / totalTasks) * 100) || 0}% del total`, icon: '✅', color: '#10b981' },
    { label: 'Story Points', value: totalPoints, change: `${completedPoints} entregados`, icon: '🎯', color: '#8b5cf6' },
  ];

  const labelClass = isDark ? 'text-slate-400' : 'text-slate-500';
  const valueClass = isDark ? 'text-slate-100' : 'text-slate-800';

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
      {stats.map((stat, i) => (
        <Card key={i} padding="md">
          <div className="flex justify-between items-start">
            <div>
              <p className={`text-xs ${labelClass} mb-1`}>{stat.label}</p>
              <h3 className={`text-2xl font-bold ${valueClass}`}>{stat.value}</h3>
              <p className="text-xs mt-1" style={{ color: stat.color }}>
                {stat.change}
              </p>
            </div>
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl"
              style={{ backgroundColor: stat.color + (isDark ? '25' : '15') }}
            >
              {stat.icon}
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
}

// Authenticated App Content - only rendered when logged in
function AuthenticatedApp() {
  const qc = useQueryClient();
  const { activeView, selectedTaskId, selectedSprintId } = useUIStore();
  const { selectedProject, selectProjectById, setProjects } = useProjectStore();
  const { logout } = useAuthStore();

  // Initialize WebSocket connection for real-time notifications
  useSocket();

  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [taskStatus, setTaskStatus] = useState(null);
  const [isClientMode, setIsClientMode] = useState(false);
  const [isProjectModalOpen, setIsProjectModalOpen] = useState(false);
  const [isClientModalOpen, setIsClientModalOpen] = useState(false);
  const [projectToEdit, setProjectToEdit] = useState(null);
  const [clientToEdit, setClientToEdit] = useState(null);

  // Data fetching
  const { data: projectsData, isLoading: projectsLoading } = useProjects();
  const projects = projectsData?.data || [];

  const projectId = selectedProject?.id;

  const { data: tasksData, isLoading: tasksLoading } = useTasks(
    { projectId },
    { enabled: !!projectId }
  );
  const tasks = tasksData?.data || [];

  const { data: sprintsData } = useSprints({ projectId }, { enabled: !!projectId });
  const sprints = sprintsData?.data || [];

  const { data: epicsData } = useEpics({ projectId }, { enabled: !!projectId });
  const epics = epicsData?.data || [];

  const { data: membersData } = useProjectMembers(projectId);
  const members = membersData?.data || [];
  const users = members.map((m) => m.user).filter(Boolean);

  const { data: clientsData } = useClients();
  const clients = clientsData?.data || [];

  const { data: taskStatusesData } = useTaskStatuses(projectId);
  const taskStatuses = taskStatusesData?.data || [];

  // Mutations
  const updateTaskStatus = useUpdateTaskStatus();
  const createTask = useCreateTask();
  const updateTask = useUpdateTask();
  const deleteTask = useDeleteTask();
  const createProject = useCreateProject();
  const updateProject = useUpdateProject();
  const createClient = useCreateClient();
  const updateClient = useUpdateClient();

  // Initialize with first project when projects load
  useEffect(() => {
    if (projects.length > 0) {
      setProjects(projects);
      if (!selectedProject) {
        selectProjectById(projects[0].id);
      }
    }
  }, [projects]);

  const handleProjectChange = (projectId) => {
    selectProjectById(projectId);
  };

  const handleTaskMove = async (taskId, newStatus) => {
    try {
      await updateTaskStatus.mutateAsync({ id: taskId, status: newStatus });
    } catch (error) {
      console.error('Error updating task status:', error);
    }
  };

  const handleAddTask = (status) => {
    setSelectedTask(null);
    setTaskStatus(status);
    setIsTaskModalOpen(true);
  };

  const handleTaskClick = (task) => {
    setSelectedTask(task);
    setTaskStatus(null);
    setIsTaskModalOpen(true);
  };

  const handleTaskSubmit = async (formData) => {
    try {
      if (selectedTask) {
        await updateTask.mutateAsync({ id: selectedTask.id, data: formData });
      } else {
        await createTask.mutateAsync({
          ...formData,
          status: taskStatus || 'backlog',
        });
      }
      setIsTaskModalOpen(false);
      setSelectedTask(null);
      setTaskStatus(null);
    } catch (error) {
      console.error('Error saving task:', error);
    }
  };

  const handleDeleteTask = async (taskId) => {
    try {
      await deleteTask.mutateAsync(taskId);
      setIsTaskModalOpen(false);
      setSelectedTask(null);
      setTaskStatus(null);
    } catch (error) {
      console.error('Error deleting task:', error);
    }
  };

  const handleProjectSubmit = async (formData) => {
    try {
      if (projectToEdit) {
        await updateProject.mutateAsync({ id: projectToEdit.id, data: formData });
      } else {
        const result = await createProject.mutateAsync(formData);
        // Select the newly created project
        if (result?.data?.id) {
          selectProjectById(result.data.id);
        }
      }
      setIsProjectModalOpen(false);
      setProjectToEdit(null);
    } catch (error) {
      console.error('Error saving project:', error);
    }
  };

  const handleEditProject = () => {
    setProjectToEdit(selectedProject);
    setIsProjectModalOpen(true);
  };

  const handleClientSubmit = async (formData) => {
    try {
      if (clientToEdit) {
        await updateClient.mutateAsync({ id: clientToEdit.id, data: formData });
      } else {
        await createClient.mutateAsync(formData);
      }
      setIsClientModalOpen(false);
      setClientToEdit(null);
    } catch (error) {
      console.error('Error saving client:', error);
    }
  };

  const handleEditClient = (client) => {
    setClientToEdit(client);
    setIsClientModalOpen(true);
  };

  const renderView = () => {
    switch (activeView) {
      case 'kanban':
        return (
          <KanbanBoard
            tasks={tasks}
            taskStatuses={taskStatuses}
            projectId={projectId}
            onTaskMove={handleTaskMove}
            onAddTask={handleAddTask}
            onTaskClick={handleTaskClick}
            isLoading={tasksLoading}
          />
        );

      case 'sprints':
        return <SprintsView projectId={projectId} />;

      case 'team':
        return <TeamView projectId={projectId} />;

      case 'metrics':
        return <MetricsView projectId={projectId} />;

      case 'roadmap':
        return <RoadmapView projectId={projectId} />;

      case 'calendar':
        return <CalendarView projectId={projectId} />;

      case 'meetings':
        return <MeetingsView projectId={projectId} />;

      case 'settings':
        return <SettingsPage />;

      case 'task-detail':
        return selectedTaskId ? (
          <TaskDetailPage
            taskId={selectedTaskId}
            sprints={sprints}
            epics={epics}
            users={users}
          />
        ) : (
          <div className="p-8 text-center text-slate-500">
            No hay tarea seleccionada
          </div>
        );

      case 'sprint-detail':
        return selectedSprintId ? (
          <SprintDetailView
            sprintId={selectedSprintId}
            projectId={projectId}
          />
        ) : (
          <div className="p-8 text-center text-slate-500">
            No hay sprint seleccionado
          </div>
        );

      case 'dashboard':
      default:
        return (
          <>
            <DashboardStats tasks={tasks} isLoading={tasksLoading} />
            <div className="mt-6">
              <h2 className="text-lg font-bold text-slate-800 dark:text-slate-100 mb-4">Vista Kanban</h2>
              <KanbanBoard
                tasks={tasks}
                taskStatuses={taskStatuses}
                projectId={projectId}
                onTaskMove={handleTaskMove}
                onAddTask={handleAddTask}
                onTaskClick={handleTaskClick}
                isLoading={tasksLoading}
              />
            </div>
          </>
        );
    }
  };

  if (projectsLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-slate-100">
        <div className="text-center">
          <Spinner size="lg" />
          <p className="mt-4 text-slate-600">Cargando proyectos...</p>
        </div>
      </div>
    );
  }

  // Client Portal Mode
  if (isClientMode) {
    return (
      <ClientPortal
        project={selectedProject}
        onBack={() => setIsClientMode(false)}
      />
    );
  }

  return (
    <MainLayout
      projects={projects}
      selectedProject={selectedProject}
      onProjectChange={handleProjectChange}
      onClientModeToggle={() => setIsClientMode(true)}
      onNewTask={() => handleAddTask('backlog')}
      onNewProject={() => {
        setProjectToEdit(null);
        setIsProjectModalOpen(true);
      }}
      onEditProject={handleEditProject}
    >
      {renderView()}

      <TaskModal
        isOpen={isTaskModalOpen}
        onClose={() => {
          setIsTaskModalOpen(false);
          setSelectedTask(null);
          setTaskStatus(null);
        }}
        onSubmit={handleTaskSubmit}
        task={selectedTask}
        projectId={projectId}
        sprints={sprints}
        epics={epics}
        users={users}
        isLoading={createTask.isPending || updateTask.isPending}
        onDelete={handleDeleteTask}
        isDeleting={deleteTask.isPending}
      />

      <ProjectModal
        isOpen={isProjectModalOpen}
        onClose={() => {
          setIsProjectModalOpen(false);
          setProjectToEdit(null);
        }}
        onSubmit={handleProjectSubmit}
        project={projectToEdit}
        clients={clients}
        isLoading={createProject.isPending || updateProject.isPending}
        onNewClient={() => {
          setClientToEdit(null);
          setIsClientModalOpen(true);
        }}
        onEditClient={handleEditClient}
      />

      <ClientModal
        isOpen={isClientModalOpen}
        onClose={() => {
          setIsClientModalOpen(false);
          setClientToEdit(null);
        }}
        onSubmit={handleClientSubmit}
        client={clientToEdit}
        isLoading={createClient.isPending || updateClient.isPending}
        onClientUpdated={() => qc.invalidateQueries({ queryKey: ['clients'] })}
      />
    </MainLayout>
  );
}

// Client Portal Wrapper - handles the complete client portal flow
function ClientPortalWrapper({ portalToken }) {
  const [isValidated, setIsValidated] = useState(false);
  const [clientData, setClientData] = useState(null);
  const [portalData, setPortalData] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handlePinSuccess = async (client) => {
    setClientData(client);
    setIsLoading(true);

    try {
      // Use authenticated endpoint after PIN validation
      const response = await clientService.getPortalDataAuthenticated(portalToken);
      if (response.success) {
        setPortalData(response.data);
        // Auto-select first project if only one
        if (response.data.projects?.length === 1) {
          setSelectedProject(response.data.projects[0]);
        }
        setIsValidated(true);
      } else {
        setError('Error al cargar los datos del portal');
      }
    } catch (err) {
      setError('Error de conexión');
    } finally {
      setIsLoading(false);
    }
  };

  // Show PIN input if not validated
  if (!isValidated) {
    if (isLoading) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-slate-800 to-slate-900 flex items-center justify-center">
          <div className="text-center">
            <Spinner size="lg" />
            <p className="mt-4 text-white">Cargando portal...</p>
          </div>
        </div>
      );
    }
    return <ClientPortalLogin token={portalToken} onSuccess={handlePinSuccess} />;
  }

  // Show project selection if multiple projects
  if (!selectedProject && portalData?.projects?.length > 1) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-800 to-slate-900 flex items-center justify-center p-4">
        <div className="w-full max-w-2xl">
          <div className="text-center mb-8">
            {portalData?.logo ? (
              <img
                src={portalData.logo}
                alt={portalData.name || 'Logo'}
                className="w-16 h-16 rounded-2xl mx-auto mb-4 object-cover"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            ) : (
              <div className="w-16 h-16 bg-gradient-to-br from-primary-500 to-secondary-700 rounded-2xl flex items-center justify-center text-3xl mx-auto mb-4">
                🚀
              </div>
            )}
            <h1 className="text-2xl font-bold text-white">Bienvenido, {clientData?.name || portalData?.name}</h1>
            <p className="text-slate-400 mt-1">Selecciona un proyecto para ver su progreso</p>
          </div>

          <div className="grid gap-4">
            {portalData.projects.map((project) => (
              <Card
                key={project.id}
                padding="lg"
                className="cursor-pointer hover:ring-2 hover:ring-primary-500 transition-all"
                onClick={() => setSelectedProject(project)}
              >
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="text-lg font-bold text-slate-800">{project.name}</h3>
                    <p className="text-sm text-slate-500">{project.description || 'Sin descripción'}</p>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-primary-600">{project.progress || 0}%</div>
                    <p className="text-xs text-slate-500">{project.completedTasks || 0}/{project.totalTasks || 0} tareas</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Show client portal with selected project
  return (
    <ClientPortal
      project={selectedProject || portalData?.projects?.[0]}
      onBack={() => {
        if (portalData?.projects?.length > 1) {
          setSelectedProject(null);
        }
      }}
      isPublicPortal={true}
      portalToken={portalToken}
      clientInfo={{
        name: portalData?.name,
        logo: portalData?.logo,
        company: portalData?.company,
      }}
    />
  );
}

ClientPortalWrapper.propTypes = {
  portalToken: PropTypes.string.isRequired,
};

// Main App Content - handles auth routing
function AppContent() {
  const { isAuthenticated, token, login, refreshUser } = useAuthStore();
  const [authError, setAuthError] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(true);

  // Check for portal token in URL
  const portalToken = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    const urlToken = params.get('token');
    // Check if we're on the portal path
    if (window.location.pathname === '/portal' && urlToken) {
      return urlToken;
    }
    return null;
  }, []);

  // Refresh user data from server on app init (ensures role and permissions are up-to-date)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const authToken = params.get('auth_token');

    // Skip refresh if this is a fresh OAuth callback (will be handled in OAuth effect)
    // or if we're on the portal
    if (authToken || portalToken) {
      setIsRefreshing(false);
      return;
    }

    // Refresh if we have a stored token to get updated permissions
    if (token) {
      refreshUser().finally(() => setIsRefreshing(false));
    } else {
      setIsRefreshing(false);
    }
  }, []);

  // Handle Microsoft OAuth callback
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const authToken = params.get('auth_token');
    const authUser = params.get('auth_user');
    const authErrorParam = params.get('auth_error');

    if (authErrorParam) {
      setAuthError(decodeURIComponent(authErrorParam));
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
      return;
    }

    if (authToken && authUser) {
      try {
        const userData = JSON.parse(decodeURIComponent(authUser));
        login(userData, authToken);
        // Clean URL first
        window.history.replaceState({}, document.title, window.location.pathname);
        // Fetch full profile with permissions after OAuth login
        refreshUser();
      } catch (err) {
        setAuthError('Error al procesar la autenticación');
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    }
  }, []);

  const handleLogin = (userData, accessToken) => {
    login(userData, accessToken);
  };

  // If portal token present, show client portal flow
  if (portalToken) {
    return <ClientPortalWrapper portalToken={portalToken} />;
  }

  // Show loading while refreshing user data
  if (isRefreshing && token) {
    return (
      <div className="flex items-center justify-center h-screen bg-slate-100">
        <div className="text-center">
          <Spinner size="lg" />
          <p className="mt-4 text-slate-600">Verificando sesión...</p>
        </div>
      </div>
    );
  }

  // Show login page if not authenticated
  if (!isAuthenticated || !token) {
    return <LoginPage onLogin={handleLogin} error={authError} />;
  }

  return <AuthenticatedApp />;
}

// Main App Component with QueryClientProvider
function App() {
  const { initTheme } = useThemeStore();

  // Initialize theme on app mount
  useEffect(() => {
    initTheme();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <AppContent />
    </QueryClientProvider>
  );
}

export default App;
